import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import SearchIcon from "@material-ui/icons/Search";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import WarningIcon from "@material-ui/icons/Warning";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import VisibilityIcon from "@material-ui/icons/Visibility";
import MessageIcon from "@material-ui/icons/Message";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
  },
  chip: {
    fontWeight: "bold",
  },
  chipComplaint: {
    backgroundColor: "#f44336",
    color: "#fff",
  },
  chipNps: {
    backgroundColor: "#ff9800",
    color: "#fff",
  },
  scoreCell: {
    fontWeight: "bold",
    color: "#f44336",
  },
  clickable: {
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },
  emptyRow: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  seenChip: {
    backgroundColor: "#4caf50",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "0.7rem",
  },
  unseenChip: {
    backgroundColor: "#9e9e9e",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "0.7rem",
  },
  notifyMsgCell: {
    maxWidth: 220,
    whiteSpace: "pre-line",
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
}));

const ComplaintLogs = () => {
  const classes = useStyles();
  const history = useHistory();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const fetchRecords = useCallback(async (page = 1, append = false) => {
    setLoading(true);
    try {
      const params = { pageNumber: page };
      if (typeFilter) params.type = typeFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const { data } = await api.get("/complaint-logs", { params });

      if (append) {
        setRecords((prev) => [...prev, ...data.records]);
      } else {
        setRecords(data.records);
      }
      setHasMore(data.hasMore);
      setPageNumber(page);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, startDate, endDate]);

  useEffect(() => {
    fetchRecords(1, false);
  }, [fetchRecords]);

  const handleOpenTicket = (record) => {
    const uuid = record.ticket?.uuid;
    if (uuid) history.push(`/tickets/${uuid}`);
  };

  const handleMarkSeen = async (record) => {
    try {
      const { data } = await api.patch(`/complaint-logs/${record.id}/seen`);
      setRecords((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      toast.success("Marcado como visto!");
    } catch (err) {
      toastError(err);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Fila de Reclamações</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.filterRow}>
            <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Tipo"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="complaint">Reclamação</MenuItem>
                <MenuItem value="nps">NPS baixo</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Data início"
              type="date"
              size="small"
              variant="outlined"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Data fim"
              type="date"
              size="small"
              variant="outlined"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <IconButton onClick={() => fetchRecords(1, false)} size="small">
              <SearchIcon />
            </IconButton>
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data/Hora</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Número</TableCell>
              <TableCell>Atendente</TableCell>
              <TableCell>Mensagem / Nota</TableCell>
              <TableCell align="center">Ticket Gestor</TableCell>
              <TableCell align="center">Visto</TableCell>
              <TableCell align="center">Ticket Cliente</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9} className={classes.emptyRow}>
                  Nenhuma reclamação registrada.
                </TableCell>
              </TableRow>
            )}
            {records.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{formatDate(r.createdAt)}</TableCell>
                <TableCell>
                  {r.type === "complaint" ? (
                    <Chip
                      icon={<WarningIcon style={{ fontSize: 14 }} />}
                      label="Reclamação"
                      size="small"
                      className={`${classes.chip} ${classes.chipComplaint}`}
                    />
                  ) : (
                    <Chip
                      icon={<StarBorderIcon style={{ fontSize: 14 }} />}
                      label={`NPS ${r.npsScore}`}
                      size="small"
                      className={`${classes.chip} ${classes.chipNps}`}
                    />
                  )}
                </TableCell>
                <TableCell>{r.contactName || "—"}</TableCell>
                <TableCell>{r.contactNumber || "—"}</TableCell>
                <TableCell>{r.agentName || "—"}</TableCell>
                <TableCell style={{ maxWidth: 240 }}>
                  <Typography variant="body2" noWrap title={r.message}>
                    {r.message || "—"}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {r.managerTicket?.uuid ? (
                    <Tooltip title="Abrir conversa com o gestor">
                      <IconButton
                        size="small"
                        onClick={() => history.push(`/tickets/${r.managerTicket.uuid}`)}
                      >
                        <MessageIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="textSecondary">—</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {r.seenAt ? (
                    <Tooltip title={`Visto por ${r.seenByName || "alguém"} em ${formatDate(r.seenAt)}`}>
                      <Chip
                        icon={<CheckCircleIcon style={{ fontSize: 14 }} />}
                        label="Visto"
                        size="small"
                        className={classes.seenChip}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Marcar como visto">
                      <IconButton size="small" onClick={() => handleMarkSeen(r)}>
                        <VisibilityIcon fontSize="small" style={{ color: "#9e9e9e" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="center">
                  {r.ticket?.uuid && (
                    <Tooltip title="Abrir ticket do cliente">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenTicket(r)}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {hasMore && (
          <div style={{ textAlign: "center", padding: 16 }}>
            <span
              style={{ cursor: "pointer", color: "#1976d2" }}
              onClick={() => fetchRecords(pageNumber + 1, true)}
            >
              {loading ? "Carregando..." : "Carregar mais"}
            </span>
          </div>
        )}
      </Paper>

    </MainContainer>
  );
};

export default ComplaintLogs;
